import Grid from "@material-ui/core/Grid";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import LocationOnIcon from "@material-ui/icons/LocationOn";
import Autocomplete from "@material-ui/lab/Autocomplete";
import parse from "autosuggest-highlight/parse";
import _ from "lodash";
import throttle from "lodash/throttle";
import PropTypes from "prop-types";
import React from "react";

function loadScript(src, position, id) {
   if (!position) {
      return;
   }

   const script = document.createElement("script");
   script.setAttribute("async", "");
   script.setAttribute("id", id);
   script.src = src;
   position.appendChild(script);
}

const autocompleteService = { current: null };

const useStyles = makeStyles((theme) => ({
   icon: {
      color: theme.palette.text.secondary,
      marginRight: theme.spacing(2),
   },
}));

export default function PlaceSearch(props) {
   const classes = useStyles();
   const [inputValue, setInputValue] = React.useState("");
   const [options, setOptions] = React.useState([]);
   const loaded = React.useRef(false);

   if (typeof window !== "undefined" && !loaded.current) {
      if (!document.querySelector("#google-maps")) {
         loadScript(
            "https://maps.googleapis.com/maps/api/js?key=AIzaSyDdtwiev0wNVg2fNuTiP1iRUVEu9asAmok&libraries=places",
            document.querySelector("head"),
            "google-maps",
         );
      }

      loaded.current = true;
   }

   const handleChange = (event) => {
      setInputValue(event.target.value);
   };

   const onChange = (event, values) => {};

   const fetch = React.useMemo(
      () =>
         throttle((request, callback) => {
            var finalRequest = {
               ...request,
               componentRestrictions: { country: "us" },
               types: ["geocode"], //SHOULD BE: ['(regions)', 'geocode'] THIS IS GOOGLE'S FAULT. TOO LIMITED. MAYBE REVISIT????
            };

            autocompleteService.current.getPlacePredictions(finalRequest, callback);
         }, 200),
      [],
   );

   React.useEffect(() => {
      let active = true;

      if (!autocompleteService.current && window.google) {
         autocompleteService.current = new window.google.maps.places.AutocompleteService();
      }
      if (!autocompleteService.current) {
         return undefined;
      }

      if (inputValue === "") {
         setOptions([]);
         return undefined;
      }

      if (inputValue.length === 5 && inputValue.match(/^[0-9]+$/) !== null) {
         fetch({ input: "postalcode " + inputValue }, (results) => {
            fetch({ input: inputValue }, (resultsNoPostalCode) => {
               if (active) {
                  if (results === null) {
                     //No postalcode results.
                     setOptions(resultsNoPostalCode || []);
                  } else if (resultsNoPostalCode === null) {
                     //No results.
                     setOptions(results || []);
                  } else {
                     //results for both. Merge.
                     results.map((result) => {
                        var placeFound = _.find(resultsNoPostalCode, (x) => x.place_id === result.place_id);

                        if (!placeFound) {
                           resultsNoPostalCode.unshift(result);
                        }
                     });

                     resultsNoPostalCode = resultsNoPostalCode.slice(0, 5);

                     setOptions(resultsNoPostalCode || []);
                  }
               }
            });
         });
      } else {
         fetch({ input: inputValue }, (results) => {
            if (active) {
               setOptions(results || []);
            }
         });
      }

      return () => {
         active = false;
      };
   }, [inputValue, fetch]);

   const onKeyUp = (event, values) => {};

   return (
      <Autocomplete
         id="google-search"
         getOptionLabel={(option) => (typeof option === "string" ? option : option.description)}
         getOptionSelected={(option, value) => option.place_id === option.place_id}
         filterOptions={(x) => x}
         options={options}
         onChange={props.onChange ? props.onChange : onChange}
         value={props.destination}
         autoComplete
         includeInputInList
         renderInput={(params) => (
            <TextField
               {...params}
               label="Select Destination.."
               variant="outlined"
               size="small"
               fullWidth
               onChange={handleChange}
            />
         )}
         autoSelect={true}
         onKeyUp={props.onKeyUp ? props.onKeyUp : onKeyUp}
         renderOption={(option) => {
            const matches = option.structured_formatting.main_text_matched_substrings;
            const parts = parse(
               option.structured_formatting.main_text,
               matches.map((match) => [match.offset, match.offset + match.length]),
            );

            return (
               <Grid container alignItems="center">
                  <Grid item>
                     <LocationOnIcon className={classes.icon} />
                  </Grid>
                  <Grid item xs>
                     {parts.map((part, index) => (
                        <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                           {part.text}
                        </span>
                     ))}

                     <Typography variant="body2" color="textSecondary">
                        {option.structured_formatting.secondary_text}
                     </Typography>
                  </Grid>
               </Grid>
            );
         }}
      />
   );
}

PlaceSearch.propTypes = {
   destination: PropTypes.string.isRequired,
   disabled: PropTypes.func,
   onChange: PropTypes.func,
   onKeyUp: PropTypes.func,
};

PlaceSearch.defaultProps = {
   destination: "",
};
